import { supabase } from '@/lib/supabase/client';

// Helper to get current user ID
async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export interface FulfillmentTracking {
  id: string;
  order_id: string;
  component_id: string;
  quantity_purchased: number;
  quantity_shipped: number;
  quantity_installed: number;
  updated_at: string;
  updated_by: string | null;
}

export const fulfillmentTrackingService = {
  async getByOrderId(orderId: string): Promise<FulfillmentTracking[]> {
    try {
      const { data, error } = await supabase
        .from('fulfillment_tracking')
        .select('*')
        .eq('order_id', orderId)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching fulfillment tracking:', error);
        return [];
      }

      return (data as FulfillmentTracking[]) || [];
    } catch (error) {
      console.error('Error fetching fulfillment tracking:', error);
      return [];
    }
  },

  async getStatus(orderId: string, componentId: string): Promise<{ purchased: number; shipped: number; installed: number } | null> {
    try {
      // Handle location-based order IDs
      let actualOrderId = orderId;
      if (orderId.startsWith('location-')) {
        const locationId = orderId.replace('location-', '');
        // Get the first order for this location
        const { data: orders, error: ordersError } = await supabase
          .from('location_orders')
          .select('id')
          .eq('location_id', locationId)
          .order('order_date', {ascending: false}).order('id', {ascending: true}).limit(1);
        
        if (ordersError || !orders || orders.length === 0) {
          return null; // No order exists, no tracking data
        }
        
        actualOrderId = orders[0]?.id ?? actualOrderId;
      }
      
      // Use maybeSingle() instead of single() to avoid 406 errors when no row exists
      const { data, error } = await supabase
        .from('fulfillment_tracking')
        .select('quantity_purchased, quantity_shipped, quantity_installed')
        .eq('order_id', actualOrderId)
        .eq('component_id', componentId)
        .maybeSingle();

      if (error) {
        // PGRST116 means no rows found - that's okay, return null
        if (error.code === 'PGRST116') {
          return null;
        }
        // Don't log 406 errors as they're expected when no data exists
        if (error.message?.includes('406') || error.code === '406') {
          return null;
        }
        console.error('Error fetching fulfillment status:', error);
        return null;
      }

      if (!data) return null;

      return {
        purchased: data.quantity_purchased,
        shipped: data.quantity_shipped,
        installed: data.quantity_installed,
      };
    } catch (error) {
      // Silently handle errors - return null so the caller can continue
      return null;
    }
  },

  async updateStatus(
    orderId: string,
    componentId: string,
    status: 'purchased' | 'shipped' | 'installed',
    quantity: number
  ): Promise<void> {
    try {
      if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Fulfillment quantity must be a nonnegative integer');
      const userId = await getCurrentUserId();
      
      // Validate orderId - if it's a location-based ID (starts with "location-"), 
      // we need to get or create a real order
      let actualOrderId = orderId;
      if (orderId.startsWith('location-')) {
        const locationId = orderId.replace('location-', '');
        // Get the first order for this location, or create one if none exists
        const { data: orders, error: ordersError } = await supabase
          .from('location_orders')
          .select('id')
          .eq('location_id', locationId)
          .order('order_date', {ascending: false}).order('id', {ascending: true}).limit(1);
        
        if (ordersError || !orders || orders.length === 0) {
          // Create a new order for tracking purposes
          const { data: newOrder, error: createError } = await supabase
            .from('location_orders')
            .insert({
              location_id: locationId,
              order_date: new Date().toISOString(),
              status: 'pending',
              notes: 'Created for fulfillment tracking',
            })
            .select('id')
            .single();
          
          if (createError || !newOrder) {
            throw new Error(`Failed to create order for tracking: ${createError?.message || 'Unknown error'}`);
          }
          
          actualOrderId = newOrder.id;
        } else {
          actualOrderId = orders[0]?.id ?? actualOrderId;
        }
      }

      // Check if tracking record exists using maybeSingle() to avoid 406 errors
      const { data: existing, error: fetchError } = await supabase
        .from('fulfillment_tracking')
        .select('*')
        .eq('order_id', actualOrderId)
        .eq('component_id', componentId)
        .maybeSingle();

      const updateData: Record<string, unknown> = {
        updated_by: userId,
      };

      if (status === 'purchased') {
        updateData.quantity_purchased = quantity;
      } else if (status === 'shipped') {
        updateData.quantity_shipped = quantity;
      } else if (status === 'installed') {
        updateData.quantity_installed = quantity;
      }

      // If no existing record (null data or PGRST116 error), create one
      const noExistingRecord = !existing || (fetchError && (fetchError.code === 'PGRST116' || fetchError.message?.includes('406')));
      
      if (noExistingRecord) {
        // Record doesn't exist, create it
        const insertData = {
          order_id: actualOrderId,
          component_id: componentId,
          quantity_purchased: status === 'purchased' ? quantity : 0,
          quantity_shipped: status === 'shipped' ? quantity : 0,
          quantity_installed: status === 'installed' ? quantity : 0,
          updated_by: userId,
        };

        const { error: insertError } = await supabase.from('fulfillment_tracking').insert(insertData);

        if (insertError) {
          console.error('Error creating fulfillment tracking:', insertError);
          throw new Error(`Failed to create fulfillment tracking: ${insertError.message}`);
        }
      } else if (existing) {
        const field = `quantity_${status}` as 'quantity_purchased' | 'quantity_shipped' | 'quantity_installed';
        const expected = existing[field];
        let current = existing;
        for (let attempt = 0; attempt < 3; attempt++) {
          const { data: updated, error: updateError } = await supabase.from('fulfillment_tracking')
            .update({ [field]: quantity, updated_by: userId })
            .eq('order_id', actualOrderId).eq('component_id', componentId)
            .eq('updated_at', current.updated_at).select().maybeSingle();
          if (updateError) throw new Error(`Failed to update fulfillment tracking: ${updateError.message}`);
          if (updated) return;
          const { data: latest, error: readError } = await supabase.from('fulfillment_tracking').select('*')
            .eq('order_id', actualOrderId).eq('component_id', componentId).maybeSingle();
          if (readError || !latest) throw new Error('Fulfillment changed. Reload before updating its totals.');
          if (latest[field] !== expected) throw new Error('This fulfillment total changed. Review the new value before updating it.');
          current = latest;
        }
        throw new Error('Fulfillment changed repeatedly. Reload before updating its totals.');
      } else if (fetchError) {
        console.error('Error fetching fulfillment tracking:', fetchError);
        throw new Error(`Failed to fetch fulfillment tracking: ${fetchError.message}`);
      }
    } catch (error) {
      console.error('Error updating fulfillment status:', error);
      throw error;
    }
  },
};
